import React from 'react';
import { columnsPhrase, nounOf, num, purchasedCount, stackLayers } from './palletCheckData';
import { C, card, sectionTitle, wood } from './styles';

// "How it should be packed": the pallet seen from above with every container where JobQ put it
// (containers[].on_pallet, inches from the pallet's top-left corner: length across, width down). A stack
// shows its tile grid (a stack with no grid: its columns, never a made-up grid); a carton is a rectangle
// labelled B1 with its unit count (or its bought items). Every container is a button: a stack opens the
// enlarged layers, a carton its inside. A container drawn smaller than a 44 px target (or not drawable)
// also gets a full-size button in the list under the map. Next to the map, the first stack from the side.

const pct = (v, of) => `${Math.max(0, Math.min(100, (Number(v) / of) * 100))}%`;
export const TOUCH_MIN = 44;

/** Units JobQ plans in a container (its zones). */
export function containerUnits(container) {
  return (container.zones || []).reduce((s, z) => s + (Number(z.units_planned) || 0), 0);
}

/** What a container holds, in words: "300 pcs", "2 assemblies", "300 pcs + 4 items", "4 items". */
export function containerContents(container, unit = 'pcs') {
  const zones = container.zones || [];
  const items = purchasedCount(container);
  const parts = [];
  if (zones.length) {
    const asm = zones.every((z) => z.assembly_id != null);
    const n = containerUnits(container);
    parts.push(asm ? `${n} assembl${n === 1 ? 'y' : 'ies'}` : `${n} ${unit}`);
  }
  if (items) parts.push(`${items} item${items === 1 ? '' : 's'}`);
  return parts.join(' + ') || 'empty';
}

function palletSize(pallet, containers) {
  let across = Number(pallet && pallet.length_in) || 0;
  let down = Number(pallet && pallet.width_in) || 0;
  if (!(across > 0 && down > 0)) {
    for (const c of containers) {
      const p = c.on_pallet || {};
      across = Math.max(across, (Number(p.x) || 0) + (Number(p.w) || 0));
      down = Math.max(down, (Number(p.y) || 0) + (Number(p.h) || 0));
    }
  }
  return { across: across || 40, down: down || 48 };
}

const drawable = (c) => c.on_pallet && Number(c.on_pallet.w) > 0 && Number(c.on_pallet.h) > 0;

/** The containers that need a button of their own under a map `width` px across. */
export function smallTargets(pallet, containers, width) {
  const list = containers || [];
  const { across } = palletSize(pallet, list.filter(drawable));
  const px = width / across;
  return list.filter((c) => !drawable(c) || Number(c.on_pallet.w) * px < TOUCH_MIN || Number(c.on_pallet.h) * px < TOUCH_MIN);
}

function StackCells({ zone, small }) {
  const layers = stackLayers(zone);
  if (!layers) return null;
  if (!layers.grid) {
    // No grid on file: its loose columns side by side, each as tall as it stands.
    return (
      <div aria-hidden="true" data-testid="map-columns" style={{ position: 'absolute', inset: 0, display: 'flex', gap: small ? 1 : 2, alignItems: 'flex-end', background: '#3D4148', padding: 2 }}>
        {layers.cells.map((c) => (
          <div key={c.n} style={{ flex: 1, height: `${Math.max(10, (c.height / Math.max(1, layers.maxHigh)) * 100)}%`, background: C.steel }} />
        ))}
      </div>
    );
  }
  return (
    <div aria-hidden="true" style={{ position: 'absolute', inset: 0, display: 'grid', gap: small ? 1 : 2, background: '#3D4148',
      gridTemplateColumns: `repeat(${layers.cols}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${layers.rows}, minmax(0, 1fr))` }}>
      {layers.cells.map((c) => (
        <div key={c.n} data-testid="map-cell" style={{ background: c.height > 0 ? C.steel : 'transparent' }} />
      ))}
    </div>
  );
}

const containerLabel = (c, unit) => (c.kind === 'stack'
  ? `${c.label || 'Stack'}: ${containerContents(c, unit)}. Open the layers.`
  : `${c.label || 'Box'}${c.short_id ? ` ${c.short_id}` : ''}: ${containerContents(c, unit)}. Open the box.`);

/** The pallet from above. `width` = px across (the height follows the pallet's shape). */
export function PalletMap({ pallet, containers, unit = 'pcs', width = 140, onOpen, small = true }) {
  const list = (containers || []).filter(drawable);
  const { across, down } = palletSize(pallet, list);
  const height = Math.round((width * down) / across);
  return (
    <div data-testid="pallet-map" style={{ width, height, background: wood(small ? 18 : 32, small ? 3 : 7), borderRadius: 4, position: 'relative', flexShrink: 0 }}>
      {list.map((c) => {
        const p = c.on_pallet;
        const isStack = c.kind === 'stack';
        const zone = (c.zones || [])[0];
        return (
          <button key={c.box_id} type="button" onClick={() => onOpen && onOpen(c)} aria-label={containerLabel(c, unit)} data-testid={`map-container-${c.box_id}`}
            style={{ position: 'absolute', left: pct(p.x, across), top: pct(p.y, down), width: pct(p.w, across), height: pct(p.h, down),
              padding: 0, cursor: 'pointer', boxSizing: 'border-box', borderRadius: 3, overflow: 'hidden',
              border: isStack ? '0' : `${c.riding_from ? '2px dashed' : '2px solid'} #8A6A3F`,
              background: isStack ? 'transparent' : '#EADBC3', color: C.ink, fontFamily: 'inherit',
              fontSize: small ? 11 : 15, fontWeight: 700, lineHeight: 1.25 }}>
            {isStack && zone ? <StackCells zone={zone} small={small} /> : (
              <span>
                {c.label || 'Box'}
                <br />
                {containerContents(c, unit)}
                {c.riding_from && c.riding_from.order_number ? <><br />{c.riding_from.order_number}</> : null}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** The side of one stack, small (the thumbnail) or big (the enlarged Side tab). */
export function SideView({ zone, width = 140, barHeight = 11, labels = false }) {
  const layers = stackLayers(zone);
  if (!layers) return null;
  const full = layers.fullLayers;
  const maxBars = 40;
  const drawFull = Math.min(full, maxBars);
  const h = full > 20 ? Math.max(3, Math.floor((barHeight * 20) / Math.min(full, maxBars))) : barHeight;
  const showLabel = labels && h >= 14;
  const topWidth = layers.topFull ? width : Math.max(Math.round(width * (layers.topCount / layers.spots)), 12);
  const per = layers.cells.filter((c) => c.height > 0).length;
  const bars = [];
  for (let i = drawFull; i >= 1; i -= 1) bars.push(i);
  return (
    <div data-testid="side-view" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: labels ? 3 : 2, alignItems: 'flex-start' }}>
      <div data-testid="side-top" style={{ width: topWidth, height: Math.max(h, labels ? 22 : h), background: C.amber, borderRadius: 2, color: '#1F1300',
        fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', paddingLeft: labels ? 8 : 0, boxSizing: 'border-box', whiteSpace: 'nowrap', overflow: 'hidden' }}>
        {labels ? `${layers.maxHigh} · top: ${layers.topCount}` : ''}
      </div>
      {bars.map((i) => (
        <div key={i} data-testid="side-layer" style={{ width, height: h, background: C.steel, borderRadius: 2, color: C.ink, fontSize: 13, fontWeight: 700,
          display: 'flex', alignItems: 'center', paddingLeft: showLabel ? 8 : 0, boxSizing: 'border-box' }}>
          {showLabel ? `${i} · ${per}` : ''}
        </div>
      ))}
      {full > drawFull && <div style={{ fontSize: 12, color: C.dim }}>…{full - drawFull} more layers below</div>}
      <div style={{ width, height: labels ? 26 : 14, background: wood(labels ? 30 : 16, labels ? 6 : 4), marginTop: labels ? 4 : 2, borderRadius: 2 }} />
    </div>
  );
}

/** A stack with no grid on file: `layout.spots` columns, each labelled with its height. */
export function ColumnsView({ zone, width = 140, height = 150, labels = false }) {
  const layers = stackLayers(zone);
  if (!layers) return null;
  const shown = layers.cells.slice(0, 24);
  return (
    <div data-testid="columns-view" style={{ width, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ height, display: 'flex', alignItems: 'flex-end', gap: 4 }}>
        {shown.map((c) => (
          <div key={c.n} data-testid="column" style={{ flex: 1, height: `${(c.height / Math.max(1, layers.maxHigh)) * 100}%`, background: C.steel, borderRadius: 2,
            color: C.ink, fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 2, overflow: 'hidden' }}>
            {labels ? c.height : ''}
          </div>
        ))}
      </div>
      {layers.spots > shown.length && <div style={{ fontSize: 12, color: C.dim }}>+{layers.spots - shown.length} more stacks</div>}
      <div style={{ height: labels ? 26 : 14, background: wood(labels ? 30 : 16, labels ? 6 : 4), borderRadius: 2 }} />
    </div>
  );
}

const tile = { ...card, padding: 10, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', color: C.ink };

function ContainerList({ title, containers, unit, onOpen, testId }) {
  if (!containers.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 6 }} data-testid={testId}>
      <h3 style={{ ...sectionTitle, fontSize: 18 }}>{title}</h3>
      {containers.map((b) => {
        const items = purchasedCount(b);
        return (
          <button key={b.box_id} type="button" onClick={() => onOpen(b)} data-testid={`box-row-${b.box_id}`}
            style={{ textAlign: 'left', minHeight: 56, ...card, padding: '12px 14px', fontFamily: 'inherit', color: C.ink, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 16, flexGrow: 1 }}>
              {b.label}{b.short_id ? ` · ${b.short_id}` : ''}{b.riding_from && b.riding_from.order_number ? ` (rides from ${b.riding_from.order_number})` : ''}
            </span>
            <span style={{ fontSize: 14 }}>{containerContents(b, unit)}{b.weight_lbs != null ? ` · ${num(b.weight_lbs, 0)} lb` : ''}</span>
            {items > 0 && <span style={{ fontSize: 13, fontWeight: 700, background: '#FEF3C7', borderRadius: 6, padding: '2px 6px' }}>{items} bought item{items === 1 ? '' : 's'}</span>}
            {b.status && <span style={{ fontSize: 13, fontWeight: 700, color: b.status === 'Sealed' ? '#14532D' : C.sub }}>{b.status}</span>}
          </button>
        );
      })}
    </div>
  );
}

export default function PackingSection({ data, onOpenStack, onOpenBox }) {
  const { noun } = nounOf(data);
  const containers = data.containers || [];
  if (!containers.length) {
    return (
      <div style={{ padding: '24px 16px 0' }}>
        <h2 style={sectionTitle}>How it should be packed</h2>
        <div style={{ ...card, marginTop: 10, fontSize: 15 }}>Nothing is planned on this {noun} yet.</div>
      </div>
    );
  }
  const unit = (data.counts && data.counts.unit) || 'pcs';
  const stacks = containers.filter((c) => c.kind === 'stack' && (c.zones || []).length);
  const boxes = containers.filter((c) => c.kind === 'box');
  const firstStack = stacks[0];
  const firstZone = firstStack ? firstStack.zones[0] : null;
  const layers = firstZone ? stackLayers(firstZone) : null;
  const open = (c) => (c.kind === 'stack' ? onOpenStack(c, (c.zones || [])[0]) : onOpenBox(c));
  const pallet = data.pallet;
  const sizeText = noun === 'pallet' && pallet && pallet.length_in && pallet.width_in ? `${Math.round(pallet.length_in)} × ${Math.round(pallet.width_in)} pallet` : '';
  const hand = containers.some((c) => (c.zones || []).some((z) => z.hand_placed));
  const twoTiles = boxes.length === 0 && !!layers;
  const mapWidth = twoTiles ? 140 : 260;
  // Stacks drawn too small to tap (or not drawable) get a button under the map; cartons are always listed.
  const smallStacks = smallTargets(pallet, stacks, mapWidth);
  const stackSideCaption = layers && (layers.grid ? `Side view · ${layers.maxHigh} layer${layers.maxHigh === 1 ? '' : 's'}` : `Side view · ${columnsPhrase(layers)}`);

  return (
    <div style={{ padding: '24px 16px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <h2 style={sectionTitle}>How it should be packed</h2>
      {twoTiles ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
          <div style={tile}>
            <PalletMap pallet={pallet} containers={containers} unit={unit} width={140} onOpen={open} />
            <div style={{ fontSize: 14, fontWeight: 600, textAlign: 'center' }}>
              Top view{stacks.length > 1 ? ` · ${stacks.length} stacks` : layers.grid ? ` · ${layers.cols} × ${layers.rows}` : ` · ${layers.spots} stack${layers.spots === 1 ? '' : 's'}`}
            </div>
            <div style={{ fontSize: 13, color: C.blue, fontWeight: 600 }}>Tap to enlarge</div>
          </div>
          <button type="button" style={{ ...tile, cursor: 'pointer', fontFamily: 'inherit' }} onClick={() => onOpenStack(firstStack, firstZone, 'side')}
            aria-label={`${stackSideCaption} (${firstStack.label || 'the stack'}). Enlarge.`}>
            <div style={{ width: 140, height: 168, display: 'flex', alignItems: 'flex-end' }}>
              {layers.grid ? <SideView zone={firstZone} width={140} /> : <ColumnsView zone={firstZone} width={140} height={140} />}
            </div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{stackSideCaption}</div>
            <div style={{ fontSize: 13, color: C.blue, fontWeight: 600 }}>Tap to enlarge</div>
          </button>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 14, color: C.sub }}>Tap a {boxes.length ? 'box' : 'stack'} to see what goes {boxes.length ? 'inside it' : 'on it'}.</div>
          <div style={{ ...tile, padding: 14 }}>
            <PalletMap pallet={pallet} containers={containers} unit={unit} width={260} small={false} onOpen={open} />
            {sizeText && <div style={{ fontSize: 13, color: C.sub }}>{sizeText}</div>}
          </div>
        </>
      )}
      {hand && (
        <div style={{ fontSize: 14, color: C.sub }}>Some pieces were put here by hand: the picture shows the plan, not how they were stacked.</div>
      )}
      <ContainerList title={`Stacks on this ${noun} (${stacks.length})`} containers={smallStacks.length ? stacks : []} unit={unit} onOpen={open} testId="stack-list" />
      <ContainerList title={noun === 'box' ? `Inside this box (${boxes.length})` : `Boxes on this pallet (${boxes.length})`} containers={boxes} unit={unit} onOpen={onOpenBox} testId="box-list" />
    </div>
  );
}
